/**
 * 地図の時間フィルタ バー
 *
 * マップ下に表示し、レンジスライダー（5分刻み）と時刻入力（1分単位）で
 * 地図に表示する時間帯を絞り込む
 */

import { Clock, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RangeSlider } from '@/components/ui/slider'
import {
  FULL_DAY_RANGE,
  type MapTimeRange,
  MINUTES_IN_DAY,
  minutesToRange,
  parseTimeToMinutes,
  rangeToMinutes,
} from '@/lib/map-time-filter'

const SLIDER_STEP_MINUTES = 5

type Props = {
  /** ルート要素の id（トグルボタンの aria-controls から参照する） */
  id?: string
  /** 現在の絞り込み（null は絞り込みなし） */
  value: MapTimeRange | null
  /** 絞り込み変更時のコールバック */
  onChange: (range: MapTimeRange | null) => void
  /** バーを閉じる */
  onClose: () => void
}

export const MapTimeFilterBar = ({ id, value, onChange, onClose }: Props) => {
  const current = value ?? FULL_DAY_RANGE
  const [startMinutes, endMinutes] = rangeToMinutes(current)

  // 入力途中の値を保持するためのローカル state（有効な値になった時点で反映）
  const [startInput, setStartInput] = useState(current.start)
  const [endInput, setEndInput] = useState(current.end)

  useEffect(() => {
    setStartInput(current.start)
    setEndInput(current.end)
  }, [current.start, current.end])

  const handleStartInput = (time: string) => {
    setStartInput(time)
    const minutes = parseTimeToMinutes(time)
    if (minutes === null) return
    onChange(minutesToRange(minutes, endMinutes))
  }

  const handleEndInput = (time: string) => {
    setEndInput(time)
    const minutes = parseTimeToMinutes(time)
    if (minutes === null) return
    onChange(minutesToRange(startMinutes, minutes))
  }

  // 入力が不正なまま離れた場合は現在の絞り込み値に戻す（表示と実データの不整合を防ぐ）
  const handleStartBlur = () => {
    if (parseTimeToMinutes(startInput) === null) setStartInput(current.start)
  }

  const handleEndBlur = () => {
    if (parseTimeToMinutes(endInput) === null) setEndInput(current.end)
  }

  return (
    <div id={id} className="bg-card space-y-3 rounded-lg border p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <Clock className="size-4" />
          時間で絞り込み
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onChange(null)}>
            解除
          </Button>
          <Button variant="ghost" size="icon" className="size-7" aria-label="時間フィルタを閉じる" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>
      </div>

      {/* レンジスライダー（5分刻み） */}
      <div className="space-y-1.5 px-1">
        <RangeSlider
          min={0}
          max={MINUTES_IN_DAY}
          step={SLIDER_STEP_MINUTES}
          value={[startMinutes, endMinutes]}
          onValueChange={([start, end]) => onChange(minutesToRange(start ?? 0, end ?? MINUTES_IN_DAY))}
          aria-label="表示する時間帯"
        />
        <div className="text-muted-foreground flex justify-between text-[10px]">
          <span>0:00</span>
          <span>6:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>24:00</span>
        </div>
      </div>

      {/* 時刻入力（1分単位） */}
      <div className="flex items-center gap-2">
        <Input
          type="time"
          aria-label="開始時刻"
          className="h-8"
          value={startInput}
          onChange={(e) => handleStartInput(e.target.value)}
          onBlur={handleStartBlur}
        />
        <span className="text-muted-foreground text-sm">〜</span>
        <Input
          type="time"
          aria-label="終了時刻"
          className="h-8"
          value={endInput}
          onChange={(e) => handleEndInput(e.target.value)}
          onBlur={handleEndBlur}
        />
      </div>
    </div>
  )
}
